import {
  type GuardarQuiz,
  type PreguntaDeQuiz,
  problemasDeQuiz,
  TIEMPOS_DE_PREGUNTA,
} from "@gestiondoc/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { queryClient, useTRPC } from "../api.ts";
import { Encabezado } from "../componentes/Encabezado.tsx";
import { LetraOpcion } from "../componentes/Reto.tsx";
import { Aviso, Boton, Campo, CLASE_ENTRADA, EstadoVacio, Tarjeta } from "../componentes/ui.tsx";
import { mensajeDe } from "../utiles.ts";

type Formulario = Omit<GuardarQuiz, "id">;

const VACIO: Formulario = { titulo: "", descripcion: "", preguntas: [] };

/** `crypto.randomUUID` no existe en http, y así se prueba desde la tablet en la red local. */
const idNuevo = () => `p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const preguntaNueva = (): PreguntaDeQuiz => ({
  id: idNuevo(),
  texto: "",
  opciones: ["", "", "", ""],
  correcta: 0,
  segundos: 20,
});

export function PaginaNuevoQuiz() {
  return <Editor inicial={VACIO} />;
}

export function PaginaEditarQuiz() {
  const { id } = useParams({ from: "/app/quices/$id/editar" });
  const trpc = useTRPC();
  const quiz = useQuery(trpc.quices.obtener.queryOptions({ id }));
  if (quiz.error) return <EstadoVacio>{mensajeDe(quiz.error)}</EstadoVacio>;
  if (!quiz.data) return <EstadoVacio>Cargando…</EstadoVacio>;
  return <Editor key={id} id={id} inicial={quiz.data} />;
}

function Editor({ id, inicial }: { id?: string; inicial: Formulario }) {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const [f, setF] = useState(() =>
    inicial.preguntas.length ? inicial : { ...inicial, preguntas: [preguntaNueva()] },
  );
  const [error, setError] = useState<string | null>(null);
  const guardar = useMutation(trpc.quices.guardar.mutationOptions());
  const borrar = useMutation(trpc.quices.borrar.mutationOptions());

  const alListado = () => {
    queryClient.invalidateQueries();
    navigate({ to: "/quices" });
  };
  const preguntas = f.preguntas.map((p) => ({
    ...p,
    // Dos casillas vacías son lo normal en verdadero o falso: no se guardan como opciones.
    opciones: p.opciones.filter((o) => o.trim()),
  }));

  async function alGuardar() {
    const problemas = problemasDeQuiz(preguntas);
    if (f.titulo.trim().length < 3) problemas.unshift("Ponle un título al reto");
    if (problemas.length > 0) return setError(problemas.join(". "));
    setError(null);
    try {
      await guardar.mutateAsync({ ...f, id, preguntas });
      alListado();
    } catch (e) {
      setError(mensajeDe(e));
    }
  }

  const cambiarPregunta = (i: number, parte: Partial<PreguntaDeQuiz>) =>
    setF((v) => ({
      ...v,
      preguntas: v.preguntas.map((p, j) => (j === i ? { ...p, ...parte } : p)),
    }));

  return (
    <div className="mx-auto max-w-[760px]">
      <Encabezado overline="Reto SMG" titulo={id ? "Editar reto" : "Nuevo reto"} />
      <div className="space-y-4">
        <Tarjeta>
          <div className="space-y-3">
            <Campo etiqueta="Título">
              <input
                value={f.titulo}
                onChange={(e) => setF({ ...f, titulo: e.target.value })}
                placeholder="Repaso de inocuidad"
                className={CLASE_ENTRADA}
              />
            </Campo>
            <Campo etiqueta="Descripción" ayuda="Opcional.">
              <input
                value={f.descripcion}
                onChange={(e) => setF({ ...f, descripcion: e.target.value })}
                className={CLASE_ENTRADA}
              />
            </Campo>
          </div>
        </Tarjeta>

        {f.preguntas.map((p, i) => (
          <Tarjeta key={p.id} titulo={`Pregunta ${i + 1}`}>
            <div className="space-y-3">
              <Campo etiqueta="Pregunta">
                <input
                  value={p.texto}
                  onChange={(e) => cambiarPregunta(i, { texto: e.target.value })}
                  className={CLASE_ENTRADA}
                />
              </Campo>
              <fieldset className="m-0 space-y-1.5 border-0 p-0">
                <legend className="mb-1 text-[11.5px] font-semibold text-texto-2">
                  Opciones: marca la correcta. Con dos basta, para verdadero o falso.
                </legend>
                {p.opciones.map((o, j) => (
                  // Las opciones no tienen id propio: su lugar en la lista es lo que las identifica.
                  // biome-ignore lint/suspicious/noArrayIndexKey: se editan en su sitio
                  <div key={j} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={p.id}
                      checked={p.correcta === j}
                      onChange={() => cambiarPregunta(i, { correcta: j })}
                      aria-label={`La opción ${j + 1} es la correcta`}
                    />
                    <LetraOpcion indice={j} />
                    <input
                      value={o}
                      onChange={(e) =>
                        cambiarPregunta(i, {
                          opciones: p.opciones.map((x, k) => (k === j ? e.target.value : x)),
                        })
                      }
                      placeholder={`Opción ${j + 1}`}
                      aria-label={`Opción ${j + 1} de la pregunta ${i + 1}`}
                      className={CLASE_ENTRADA}
                    />
                  </div>
                ))}
              </fieldset>
              <div className="flex flex-wrap items-end gap-3 border-t border-borde pt-3">
                <Campo etiqueta="Tiempo en vivo" className="w-[140px]">
                  <select
                    value={p.segundos}
                    onChange={(e) => cambiarPregunta(i, { segundos: Number(e.target.value) })}
                    className={CLASE_ENTRADA}
                  >
                    {TIEMPOS_DE_PREGUNTA.map((s) => (
                      <option key={s} value={s}>
                        {s} segundos
                      </option>
                    ))}
                  </select>
                </Campo>
                <Boton
                  variante="peligro"
                  compacto
                  className="ml-auto"
                  disabled={f.preguntas.length === 1}
                  onClick={() => setF({ ...f, preguntas: f.preguntas.filter((_, j) => j !== i) })}
                >
                  Quitar pregunta
                </Boton>
              </div>
            </div>
          </Tarjeta>
        ))}
        <Boton
          variante="secundario"
          className="w-full"
          onClick={() => setF({ ...f, preguntas: [...f.preguntas, preguntaNueva()] })}
        >
          Agregar pregunta
        </Boton>

        {error && <Aviso>{error}</Aviso>}

        <div className="flex flex-wrap items-center justify-end gap-2">
          {id && (
            <Boton
              variante="peligro"
              className="mr-auto"
              disabled={borrar.isPending}
              onClick={async () => {
                await borrar.mutateAsync({ id });
                alListado();
              }}
            >
              Borrar reto
            </Boton>
          )}
          <Boton disabled={guardar.isPending} onClick={() => void alGuardar()}>
            Guardar reto
          </Boton>
        </div>
      </div>
    </div>
  );
}
